/**
 * Company Record — append-only snapshots of what a research run learned.
 *
 * The contact document keeps the latest `researchIntelligence`, potential
 * analysis, Instagram, SEO and website scan, because mail generation and the
 * table read those fields. This module freezes a compact copy of each run so
 * a later scan does not erase the earlier one.
 *
 * Fact keys are stable on purpose. A future pass can diff two snapshots of
 * the same source without re-reading the raw crawl.
 *
 * Twin file: src/shared/intelligenceLog.js — keep the fact keys identical.
 */

const SOURCE_META = {
  web_research: { label: 'Web Research', icon: 'fa-microscope', brand: false },
  potential: { label: 'Potential', icon: 'fa-bullseye', brand: false },
  instagram: { label: 'Instagram', icon: 'fa-instagram', brand: true },
  seo: { label: 'SEO', icon: 'fa-magnifying-glass-chart', brand: false },
  website: { label: 'Website', icon: 'fa-desktop', brand: false },
  google_reviews: { label: 'Google Reviews', icon: 'fa-star-half-stroke', brand: false },
};

const text = (value, max = 180) => {
  if (value == null) return '';
  const raw = typeof value === 'string' ? value : String(value);
  const cleaned = raw.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const lower = cleaned.toLowerCase();
  if (lower === 'unknown' || lower === 'null' || lower === 'n/a' || lower === '-') return '';
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1).trim()}…`;
};

const asObject = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return null;
};

const fact = (key, label, value) => {
  const cleaned = text(value, 120);
  if (!cleaned) return null;
  return { key, label, value: cleaned };
};

const pushNotes = (notes, items, mapItem) => {
  if (!Array.isArray(items)) return;
  items.forEach((item) => {
    const line = mapItem(item);
    if (line && !notes.includes(line)) notes.push(line);
  });
};

const snapshotWebResearch = (ri, fallbackAt = null) => {
  if (!ri || typeof ri !== 'object') return null;
  const stats = ri.quickStats && typeof ri.quickStats === 'object' ? ri.quickStats : {};
  const shop = ri.shopIntelligence && typeof ri.shopIntelligence === 'object' ? ri.shopIntelligence : null;
  const linkedinCount = ri.linkedinEmployeesFound
    || (ri.linkedinCompany && ri.linkedinCompany.employeesFound)
    || null;
  const imprint = ri.websiteFacts && ri.websiteFacts.imprint ? ri.websiteFacts.imprint : null;
  const facts = [
    fact('employees', 'Employees', stats.employees),
    fact('revenue', 'Revenue', stats.revenue),
    fact('founded', 'Founded', stats.founded),
    fact('location', 'Location', stats.location),
    fact('industry', 'Industry fit', ri.industryFit),
    fact('size', 'Company size', ri.companySizeEstimate),
    fact('leadScore', 'Lead score', ri.leadScore != null ? String(ri.leadScore) : ''),
    fact('rating', 'Rating', ri.leadRating),
    fact('presence', 'Online presence', ri.onlinePresenceScore != null ? String(ri.onlinePresenceScore) : ''),
    fact('budget', 'Budget', ri.budgetIndicators),
    fact('shop', 'Shop', shop && shop.platform),
    fact('linkedin', 'LinkedIn people', linkedinCount != null ? String(linkedinCount) : ''),
    fact('decisionMakers', 'Decision makers', ri.decisionMakerCount ? String(ri.decisionMakerCount) : ''),
    fact('roles', 'Roles', Array.isArray(ri.decisionMakerRoles) ? ri.decisionMakerRoles.slice(0, 4).join(', ') : ''),
    fact('management', 'Management (imprint)', imprint && Array.isArray(imprint.managers) ? imprint.managers.map((m) => m && m.name).filter(Boolean).slice(0, 3).join(', ') : ''),
    fact('register', 'Register', imprint && imprint.registerId ? [imprint.registerId, imprint.registerCourt].filter(Boolean).join(', ') : ''),
  ].filter(Boolean);

  const notes = [];
  const companyInfo = text(ri.companyInfo, 280);
  pushNotes(notes, ri.newestSignals, (item) => {
    if (!item || !item.title) return '';
    const when = text(item.date, 10);
    return `Signal: ${text(item.title, 120)}${when ? ` (${when})` : ''}`;
  });
  pushNotes(notes, ri.keyFacts, (item) => text(item, 180));
  pushNotes(notes, ri.buySignals, (item) => {
    if (!item) return '';
    if (typeof item === 'string') return text(item, 180);
    const signal = text(item.signal, 140);
    if (!signal) return '';
    const evidence = text(item.evidence, 80);
    return evidence ? `${signal} — ${evidence}` : signal;
  });
  pushNotes(notes, ri.latestNews, (item) => {
    if (!item) return '';
    const headline = text(item.headline || item, 140);
    if (!headline) return '';
    const when = text(item.date, 40);
    return when ? `${headline} (${when})` : headline;
  });
  if (Array.isArray(ri.modules) && ri.modules.length) {
    const modules = ri.modules.map((m) => text(m, 40)).filter(Boolean).slice(0, 6);
    if (modules.length) notes.push(`Modules: ${modules.join(', ')}`);
  }

  if (!facts.length && !notes.length && !companyInfo) return null;
  const headline = companyInfo || text(ri.industryFit, 140) || (facts[0] ? `${facts[0].label}: ${facts[0].value}` : 'Web Research');
  return {
    capturedAt: ri.researchedAt || fallbackAt || null,
    headline,
    facts: facts.slice(0, 16),
    notes: notes.slice(0, 8),
  };
};

const snapshotPotential = (contact) => {
  const analysis = asObject(contact && contact.potential_analysis) || {};
  const score = typeof analysis.score === 'number'
    ? analysis.score
    : (typeof contact?.potential_score === 'number' ? contact.potential_score : null);
  const bullets = Array.isArray(analysis.bullets) ? analysis.bullets : [];
  const facts = [
    fact('score', 'Score', score != null ? `${score}/10` : ''),
    fact('fit', 'Fit', analysis.fitLabel),
    fact('size', 'Size', analysis.sizeAssessment),
    fact('response', 'Response chance', analysis.responseChance),
    fact('confidence', 'Data', analysis.dataConfidence),
  ].filter(Boolean);
  const notes = [];
  pushNotes(notes, bullets, (item) => text(item, 180));
  const summary = text(analysis.summary, 220);
  if (score == null && !summary && !facts.length && !notes.length) return null;
  const fit = text(analysis.fitLabel, 60);
  const headline = score != null
    ? `${score}/10${fit ? ` · ${fit}` : ''}`
    : (summary || fit || 'Potential');
  return {
    capturedAt: analysis.analyzedAt || null,
    headline,
    facts: facts.slice(0, 16),
    notes: notes.slice(0, 8),
  };
};

const snapshotInstagram = (ri, followerCount = null) => {
  const ig = ri && ri.instagramInsight ? ri.instagramInsight : null;
  const meta = ri && ri.instagramMeta && typeof ri.instagramMeta === 'object' ? ri.instagramMeta : {};
  const followers = meta.followerCount ?? meta.followers ?? meta.follower_count ?? followerCount;
  if (!ig && followers == null && !meta.fetchedAt) return null;
  let status = '';
  if (ig?.isDeadAccount) status = 'Inactive';
  else if (ig?.isActive) status = 'Active';
  else if (ig?.postedInLast30Days) status = 'Active in 30 days';
  const facts = [
    fact('followers', 'Followers', followers != null ? String(followers) : ''),
    fact('activity', 'Activity', ig?.activityScore != null ? `${ig.activityScore}/10` : ''),
    fact('engagement', 'Engagement', ig?.engagementScore != null ? `${ig.engagementScore}/10` : ''),
    fact('postsPerWeek', 'Posts / week', ig?.avgPostsPerWeek != null ? String(ig.avgPostsPerWeek) : ''),
    fact('tone', 'Tone', ig?.tone),
    fact('status', 'Status', status),
    fact('trend', 'Engagement trend', ig?.engagementTrend && ig.engagementTrend !== 'stable' ? ig.engagementTrend : ''),
    fact('language', 'Language', ig?.language),
    fact('handle', 'Handle', meta.handle ? `@${String(meta.handle).replace(/^@/, '')}` : ''),
  ].filter(Boolean);
  const notes = [];
  pushNotes(notes, ig?.uniqueFacts, (item) => text(item, 180));
  pushNotes(notes, ig?.topics, (item) => text(item, 40));
  const angle = text(ig?.salesAngle, 180);
  if (angle) notes.push(angle);
  const summary = text(ig?.summary, 220);
  if (!facts.length && !notes.length && !summary) return null;
  return {
    capturedAt: normalizeCapturedAt(meta.fetchedAt)
      || normalizeCapturedAt(ig && (ig.fetchedAt || ig.analyzedAt || ig.updatedAt))
      || null,
    headline: summary || (meta.handle ? `@${String(meta.handle).replace(/^@/, '')}` : 'Instagram'),
    facts: facts.slice(0, 16),
    notes: notes.slice(0, 8),
  };
};

const snapshotSeo = (seo) => {
  if (!seo || typeof seo !== 'object') return null;
  const data = seo.data && typeof seo.data === 'object' ? seo.data : {};
  const visibility = data.core?.visibility?.value;
  const trend = data.visibilityTrend?.changePct;
  const facts = [
    fact('visibility', 'Visibility', visibility != null && visibility !== '' ? Number(visibility).toFixed(2) : ''),
    fact('keywords', 'Keywords', data.core?.keywordsTotal != null ? String(data.core.keywordsTotal) : ''),
    fact('top10', 'Top 10', data.core?.keywordsTop10 != null ? String(data.core.keywordsTop10) : ''),
    fact('visitors', 'Visitors / month', data.trafficValue?.estimatedTrafficMonthly != null ? String(data.trafficValue.estimatedTrafficMonthly) : ''),
    fact('trafficValue', 'Traffic value €', data.trafficValue?.estimatedValueMonthly != null ? String(data.trafficValue.estimatedValueMonthly) : ''),
    fact('backlinks', 'Backlinks', data.backlinks?.total != null ? String(data.backlinks.total) : ''),
    fact('trend', 'Visibility trend', trend != null ? `${trend}%` : ''),
    fact('country', 'Country', seo.country),
  ].filter(Boolean);
  const notes = [];
  const competitor = data.competitors?.[0]?.domain;
  if (competitor) notes.push(`Competitor: ${text(competitor, 80)}`);
  const chance = data.opportunities?.[0];
  if (chance?.keyword) {
    const pos = chance.position != null ? ` (pos ${chance.position})` : '';
    notes.push(`Chance: ${text(chance.keyword, 80)}${pos}`);
  }
  if (!facts.length && !notes.length) return null;
  const vis = facts.find((item) => item.key === 'visibility');
  return {
    capturedAt: seo.scannedAt || null,
    headline: vis ? `Visibility ${vis.value}` : (text(seo.domain, 80) || 'SEO scan'),
    facts: facts.slice(0, 16),
    notes: notes.slice(0, 8),
  };
};

const snapshotWebsite = (wa, fallbackAt = null) => {
  if (!wa || typeof wa !== 'object') return null;
  const age = wa.websiteAge || wa.website_age || null;
  const isShop = wa.is_shop === true || wa.isShop === true;
  const facts = [
    fact('cms', 'CMS', wa.cms),
    fact('version', 'Version', wa.version_estimate || wa.versionEstimate),
    fact('industry', 'Industry', wa.industry),
    fact('products', 'Products', wa.product_count ?? wa.productCount),
    fact('categories', 'Categories', wa.category_count ?? wa.categoryCount),
    fact('age', 'Website age', age && (age.label || age.estimatedEra || age.estimated_era)),
    fact('shop', 'Shop', isShop ? 'Yes' : ''),
    fact('city', 'City', wa.city),
  ].filter(Boolean);
  const notes = [];
  const angle = text(wa.recommended_sales_angle || wa.recommendedSalesAngle, 180);
  if (angle) notes.push(angle);
  if (!facts.length && !notes.length) return null;
  const cms = text(wa.cms, 40);
  const industry = text(wa.industry, 60);
  return {
    capturedAt: wa.analyzedAt || wa.analyzed_at || fallbackAt || null,
    headline: [cms, industry].filter(Boolean).join(' · ') || 'Website scan',
    facts: facts.slice(0, 16),
    notes: notes.slice(0, 8),
  };
};

const snapshotGoogleReviews = (audit) => {
  if (!audit || typeof audit !== 'object' || !audit.place) return null;
  const place = audit.place || {};
  const stats = audit.stats || {};
  const analysis = audit.analysis || {};
  const complaints = Array.isArray(analysis.complaints) ? analysis.complaints : [];
  const strengths = Array.isArray(analysis.strengths) ? analysis.strengths : [];
  const facts = [
    fact('rating', 'Rating', place.rating != null ? `${place.rating}` : ''),
    fact('reviews', 'Reviews', place.reviewCount != null ? String(place.reviewCount) : ''),
    fact('negativeShare', '1–2 star share', stats.negativeShare != null ? `${stats.negativeShare}%` : ''),
    fact('ownerReplies', 'Owner replies', stats.ownerReplyRate != null ? `${stats.ownerReplyRate}%` : ''),
    fact('trend', 'Recent trend', stats.trend && stats.trend !== 'stable' ? stats.trend : ''),
    fact('sentiment', 'Sentiment', analysis.sentiment),
    fact('topComplaint', 'Top complaint', complaints[0] && complaints[0].theme),
    fact('topPraise', 'Top praise', strengths[0] && strengths[0].theme),
  ].filter(Boolean);
  const notes = [];
  pushNotes(notes, complaints, (item) => (item && item.theme ? `Complaint: ${text(item.theme, 120)}` : ''));
  pushNotes(notes, strengths, (item) => (item && item.theme ? `Praised: ${text(item.theme, 120)}` : ''));
  const angle = text(analysis.salesAngle, 180);
  if (angle) notes.push(angle);
  if (!facts.length && !notes.length) return null;
  const summary = text(analysis.summary, 220);
  return {
    capturedAt: audit.auditedAt || null,
    headline: summary || `${place.rating != null ? `${place.rating}★` : ''} ${place.name || 'Google Reviews'}`.trim(),
    facts: facts.slice(0, 16),
    notes: notes.slice(0, 8),
  };
};

const snapshotFromContact = (source, contact) => {
  if (!contact || !SOURCE_META[source]) return null;
  if (source === 'web_research') return snapshotWebResearch(contact.researchIntelligence, contact.spy_last_updated || null);
  if (source === 'potential') return snapshotPotential(contact);
  if (source === 'instagram') return snapshotInstagram(contact.researchIntelligence, contact.lead_instagram_followers);
  if (source === 'seo') return snapshotSeo(contact.researchIntelligence && contact.researchIntelligence.seoAnalysis);
  if (source === 'website') return snapshotWebsite(contact.websiteAnalysis, contact.website_analysis_at || null);
  if (source === 'google_reviews') return snapshotGoogleReviews(contact.researchIntelligence && contact.researchIntelligence.googleReviewAudit);
  return null;
};

const fingerprint = (event) => {
  const facts = (event?.facts || []).map((item) => `${item.key}=${item.value}`).join('|');
  const notes = (event?.notes || []).join('|');
  return `${facts}##${notes}`;
};

const timeValue = (event) => {
  const iso = normalizeCapturedAt(event?.capturedAt);
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
};

// On-file facts with no research timestamp still belong to this contact.
// Dating them at creation keeps them from sorting before the contact exists.
const contactCapturedAt = (contact) => normalizeCapturedAt(
  contact?._transferredAt || contact?.imported_at || contact?.timestamp
  || contact?.created_at || contact?.lead_added_at || contact?.lead_since
);

// Rules only accept a short string. Dates from older contacts, timestamps
// and locale strings are folded into one ISO value before the write.
const normalizeCapturedAt = (value) => {
  if (value == null || value === '') return null;
  let date = null;
  if (value instanceof Date) date = value;
  else if (typeof value === 'object' && typeof value.toDate === 'function') date = value.toDate();
  else if (typeof value === 'number' && Number.isFinite(value)) date = new Date(value);
  else if (typeof value === 'string') {
    const parsed = Date.parse(value.trim());
    if (Number.isFinite(parsed)) date = new Date(parsed);
  }
  if (!date || !Number.isFinite(date.getTime())) return null;
  return date.toISOString();
};

const cleanEvent = (event) => {
  let capturedAt = normalizeCapturedAt(event.capturedAt);
  if (!capturedAt && event.origin === 'run') capturedAt = new Date().toISOString();
  if (!capturedAt) return null;
  const facts = (event.facts || []).slice(0, 16).map((item) => ({
    key: text(item && item.key, 40),
    label: text(item && item.label, 60),
    value: text(item && item.value, 120),
  })).filter((item) => item.key && item.label && item.value);
  return {
    source: event.source,
    capturedAt,
    origin: event.origin,
    headline: text(event.headline, 220) || SOURCE_META[event.source].label,
    facts,
    notes: (event.notes || []).map((note) => text(note, 180)).filter(Boolean).slice(0, 8),
  };
};

/**
 * Events to append for one finished run.
 * The first time a source is logged, the contact's current snapshot is kept
 * as a baseline so the run that is about to overwrite it is not the only copy.
 * A repeat within two minutes with the same facts is ignored.
 */
const planCompanyRecord = ({
  source,
  beforeContact,
  afterContact,
  alreadyLogged = false,
  latestEvent = null,
  capturedAt = null,
} = {}) => {
  if (!SOURCE_META[source]) return [];
  const next = snapshotFromContact(source, afterContact);
  if (!next) return [];
  next.capturedAt = normalizeCapturedAt(capturedAt || next.capturedAt) || new Date().toISOString();

  const events = [];
  if (!alreadyLogged) {
    const baseline = snapshotFromContact(source, beforeContact);
    if (baseline) baseline.capturedAt = normalizeCapturedAt(baseline.capturedAt);
    if (
      baseline
      && baseline.capturedAt
      && baseline.capturedAt !== next.capturedAt
      && fingerprint(baseline) !== fingerprint(next)
    ) {
      const baselineEvent = cleanEvent({ ...baseline, source, origin: 'baseline' });
      if (baselineEvent) events.push(baselineEvent);
    }
  }

  const run = cleanEvent({ ...next, source, origin: 'run' });
  if (!run) return events;
  if (latestEvent && fingerprint(latestEvent) === fingerprint(run)) {
    const gap = Math.abs(timeValue(latestEvent) - timeValue(run));
    if (gap < 2 * 60 * 1000) return events;
  }
  events.push(run);
  return events;
};

const diffFacts = (olderFacts, newerFacts) => {
  const previous = new Map((olderFacts || []).map((item) => [item.key, item]));
  const changes = [];
  (newerFacts || []).forEach((item) => {
    const before = previous.get(item.key);
    if (!before) {
      changes.push({ key: item.key, label: item.label, from: null, to: item.value, kind: 'added' });
      return;
    }
    if (before.value === item.value) return;
    const fromNum = Number(String(before.value).replace(/[^\d.-]/g, ''));
    const toNum = Number(String(item.value).replace(/[^\d.-]/g, ''));
    let kind = 'changed';
    if (Number.isFinite(fromNum) && Number.isFinite(toNum) && fromNum !== toNum) {
      kind = toNum > fromNum ? 'up' : 'down';
    }
    changes.push({ key: item.key, label: item.label, from: before.value, to: item.value, kind });
  });
  (olderFacts || []).forEach((item) => {
    if ((newerFacts || []).some((next) => next.key === item.key)) return;
    changes.push({ key: item.key, label: item.label, from: item.value, to: null, kind: 'removed' });
  });
  return changes;
};

const diffNotes = (olderNotes, newerNotes) => {
  const previous = new Set(olderNotes || []);
  const next = new Set(newerNotes || []);
  return {
    added: (newerNotes || []).filter((note) => note && !previous.has(note)).slice(0, 5),
    removed: (olderNotes || []).filter((note) => note && !next.has(note)).slice(0, 3),
  };
};

const decorateTimeline = (events) => {
  const list = (events || []).filter((event) => event && event.source);
  const asc = [...list].sort((a, b) => timeValue(a) - timeValue(b) || String(a.capturedAt || '').localeCompare(String(b.capturedAt || '')));
  const bySource = {};
  const decorated = asc.map((event) => {
    const prev = bySource[event.source];
    bySource[event.source] = event;
    const noteDiff = prev ? diffNotes(prev.notes, event.notes) : { added: [], removed: [] };
    return {
      ...event,
      id: event.id || `${event.source}-${event.capturedAt || 'undated'}-${event.origin || 'run'}`,
      changes: prev ? diffFacts(prev.facts, event.facts) : [],
      addedNotes: noteDiff.added,
      removedNotes: noteDiff.removed,
      isFirst: !prev,
    };
  });
  return decorated.sort((a, b) => timeValue(b) - timeValue(a));
};

/**
 * Stored runs win. Sources that have never been logged still appear from the
 * fields already saved on the contact, dated with their original timestamp.
 */
const visibleTimeline = (stored, contact) => {
  const newestLogged = {};
  (stored || []).forEach((event) => {
    if (!event || !event.source) return;
    const prev = newestLogged[event.source];
    if (!prev || timeValue(event) >= timeValue(prev)) newestLogged[event.source] = event;
  });
  const filedAt = contactCapturedAt(contact);
  const synthesized = Object.keys(SOURCE_META).map((source) => {
    const snap = snapshotFromContact(source, contact);
    if (!snap) return null;
    const capturedAt = normalizeCapturedAt(snap.capturedAt) || filedAt;
    const logged = newestLogged[source];
    if (logged && timeValue({ capturedAt }) <= timeValue(logged)) return null;
    return {
      ...snap,
      capturedAt,
      source,
      origin: 'current',
      id: `current-${source}`,
    };
  }).filter(Boolean);
  return decorateTimeline([...(stored || []), ...synthesized]);
};

export {
  SOURCE_META,
  snapshotFromContact,
  planCompanyRecord,
  decorateTimeline,
  visibleTimeline,
  fingerprint,
};
